'use strict';

import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
	StyleSheet,
	View,
	Image,
	TouchableOpacity,
	Text,
	Modal,
	Platform,
	PanResponder,
	Easing,
	Animated,
	useWindowDimensions,
} from 'react-native';
import * as FileSystem from 'expo-file-system/legacy';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { MaterialIcons } from '@expo/vector-icons';
import type { ImageStyle, ViewStyle } from 'react-native';
import errorHandler from 'src/core/utils/errorHandler';
import { useServicesStore } from 'src/domain/stores/useServicesStore';
import { useContentWidth } from 'src/presentation/hooks/useContentWidth';
import Translations from 'src/core/utils/Translations';
import Dimens from 'src/core/utils/Dimens';
import Colors from 'src/core/utils/Colors';
import Images from 'src/core/utils/Images';

interface ImageViewerProps {
	shown: boolean;
	onClose: () => void;
	imageUrls: string[];
	index: number;
	failedUrl?: string;
	eventName?: string;
}

interface ImageInfo {
	status: string;
	url: string;
	width: number;
	height: number;
	scalable: Animated.Value;
	animatedX: Animated.Value;
}

/** Three platforms, three places a saved file ends up. Read per platform, not per render. */
function savedMessage(): string {
	if (Platform.OS === 'ios') {
		return Translations.successDownloadedMessageIos;
	}

	return Platform.OS === 'web' ? Translations.successDownloadedMessageWeb : Translations.successDownloadedMessageAndroid;
}

export default function ImageViewer({ shown, onClose, imageUrls, index, failedUrl, eventName }: ImageViewerProps) {
	// The viewer lives inside the app's own column, so the slides are as wide as that column. The
	// column is full height, so the height still comes from the window.
	const contentWidth = useContentWidth();
	const { mediaLibrary } = useServicesStore();
	const { height: windowHeight } = useWindowDimensions();
	const insets = useSafeAreaInsets();
	// The viewer is a full-screen Modal; reserve the top inset (notch/status bar) for the controls.
	const topInset = insets.top || (Platform.OS === 'ios' ? 47 : 24);

	const [curIndex, setCurIndex] = useState(0);
	const [, setMidIndex] = useState(0);
	const [, setMaxIndex] = useState(0);
	const [imagesInfo, setImagesInfo] = useState<ImageInfo[]>([]);
	const [imageLoaded, setImageLoaded] = useState(false);

	const fadeAnim = useRef(new Animated.Value(0)).current;
	const scalable = useRef(new Animated.Value(0)).current;
	const rotateValue = useRef(new Animated.Value(0)).current;

	const isClick = useRef(true);
	const lastClickTime = useRef(0);
	const clickTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
	const standardPositionX = useRef(0);
	const positionX = useRef(0);
	const animatedPositionX = useRef(new Animated.Value(0)).current;
	const imgScale = useRef(1);
	const horizontalWholeCounter = useRef(0);
	const maxOffsetX = useRef(0);
	const layoutImage = useRef<Record<number, { width: number; height: number }>>({});
	const isReachedBorder = useRef(false);
	const zoomCurrentDistance = useRef(0);
	const zoomLastDistance = useRef<number | undefined>(undefined);

	const curIndexRef = useRef(curIndex);
	const imagesInfoRef = useRef(imagesInfo);
	const imageLoadedRef = useRef(imageLoaded);

	curIndexRef.current = curIndex;
	imagesInfoRef.current = imagesInfo;
	imageLoadedRef.current = imageLoaded;

	const imagePanResponder = useRef(
		PanResponder.create({
			onStartShouldSetPanResponder: () => true,
			onStartShouldSetPanResponderCapture: () => true,
			onMoveShouldSetPanResponder: () => true,
			onMoveShouldSetPanResponderCapture: () => true,
			onPanResponderTerminationRequest: () => false,

			onPanResponderGrant: (evt) => {
				zoomLastDistance.current = undefined;
				if (evt.nativeEvent.changedTouches.length <= 1) {
					isClick.current = true;
				} else {
					isClick.current = false;
					lastClickTime.current = 0;
				}
			},

			onPanResponderMove: (evt, gestureState) => {
				const touches = evt.nativeEvent.changedTouches;
				const ci = curIndexRef.current;
				const imageInfo = imagesInfoRef.current[ci]!;

				if (touches.length <= 1) {
					if (isClick.current) {
						isClick.current = false;
						lastClickTime.current = 0;
					}

					if (imgScale.current === 1.5) {
						const layout = layoutImage.current[ci];
						const imgWidth = layout?.width ?? 0;
						maxOffsetX.current = Math.ceil((imgWidth * 0.5) / 2 - 30);

						let x = gestureState.dx + horizontalWholeCounter.current;
						isReachedBorder.current = false;

						if (x >= maxOffsetX.current && gestureState.dx > 0) {
							isReachedBorder.current = true;
							x = maxOffsetX.current;
						}

						if (x <= -maxOffsetX.current && gestureState.dx < 0) {
							isReachedBorder.current = true;
							x = -maxOffsetX.current;
						}

						!isReachedBorder.current && imageInfo.animatedX.setValue(x);

						if (isReachedBorder.current) {
							positionX.current = gestureState.dx + standardPositionX.current;
							animatedPositionX.setValue(positionX.current);
						}
					} else {
						positionX.current = gestureState.dx + standardPositionX.current;
						animatedPositionX.setValue(positionX.current);
					}
				} else {
					let minX: number, maxX: number;
					let minY: number, maxY: number;

					const t0 = touches[0]!;
					const t1 = touches[1]!;
					if (t0.locationX > t1.locationX) {
						minX = t1.pageX;
						maxX = t0.pageX;
					} else {
						minX = t0.pageX;
						maxX = t1.pageX;
					}

					if (t0.locationY > t1.locationY) {
						minY = t1.pageY;
						maxY = t0.pageY;
					} else {
						minY = t0.pageY;
						maxY = t1.pageY;
					}

					const widthDistance = maxX - minX;
					const heightDistance = maxY - minY;
					const diagonalDistance = Math.sqrt(widthDistance * widthDistance + heightDistance * heightDistance);
					zoomCurrentDistance.current = Number(diagonalDistance.toFixed(1));

					if (zoomLastDistance.current !== undefined) {
						const distanceDiff = (zoomCurrentDistance.current - zoomLastDistance.current) / 200;
						let zoom = imgScale.current + distanceDiff;

						if (zoom < 1) zoom = 1;
						if (zoom > 1.5) zoom = 1.5;

						imgScale.current = zoom;
						imageInfo.scalable.setValue(imgScale.current);
					}
					zoomLastDistance.current = zoomCurrentDistance.current;
				}
			},

			onPanResponderRelease: (evt, gestureState) => {
				if (evt.nativeEvent.changedTouches.length <= 1) {
					if (isClick.current) {
						if (lastClickTime.current && new Date().getTime() - lastClickTime.current < 300) {
							if (clickTimer.current) clearTimeout(clickTimer.current);
							const ci = curIndexRef.current;
							const imageInfo = imagesInfoRef.current[ci]!;

							if (imgScale.current === 1) {
								imgScale.current = 1.5;
							} else {
								imgScale.current = 1;
								imageInfo.animatedX.setValue(0);
							}

							Animated.timing(imageInfo.scalable, {
								toValue: imgScale.current,
								duration: 300,
								useNativeDriver: true,
							}).start(() => {
								horizontalWholeCounter.current = 0;
								maxOffsetX.current = 0;
								isReachedBorder.current = false;
							});

							lastClickTime.current = 0;
							return;
						}
						lastClickTime.current = new Date().getTime();
					}

					if (imgScale.current === 1.5) {
						if (!isReachedBorder.current) {
							horizontalWholeCounter.current += gestureState.dx;
							return;
						}
					}

					if (gestureState.dx < -80) {
						next(curIndexRef.current);
					} else {
						resetPosition();
					}

					if (gestureState.dx > 80) {
						prev(curIndexRef.current);
					} else {
						resetPosition();
					}
				}
			},

			onPanResponderTerminate: () => {},
		}),
	).current;

	// eslint-disable-next-line react-hooks/exhaustive-deps
	useEffect(() => {
		if (shown) {
			init(index, imageUrls);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [shown]);

	const lastWidthRef = useRef(contentWidth);

	// The slide offsets below are measured in whole viewport widths, so a width change (rotation,
	// split screen) would leave the carousel resting between two images. Recompute the resting
	// offset for the image on screen; the width guard keeps this a no-op on mount, where init()
	// already positions the carousel.
	useEffect(() => {
		if (lastWidthRef.current === contentWidth) {
			return;
		}
		lastWidthRef.current = contentWidth;
		if (!shown) {
			return;
		}

		const len = imageUrls.length;
		const px = (Math.floor((len - 1) / 2) - curIndex) * contentWidth;
		const stdX = len % 2 === 0 ? px + contentWidth / 2 : px;

		// positionX gets stdX here, while init() assigns it px. They differ only for an even number
		// of images, and stdX is what the carousel is actually rendered at -- so this matches the
		// screen. init()'s px looks like a latent inconsistency, but it is a native path with no
		// coverage, so it is not being touched here.
		positionX.current = stdX;
		standardPositionX.current = stdX;
		animatedPositionX.setValue(stdX);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [contentWidth]);

	const sizedStyles = useMemo(
		(): {
			column: ViewStyle;
			titleBar: ViewStyle;
			loadingImgView: ViewStyle;
			loadingImg: ImageStyle;
			loadedImg: ViewStyle;
			failedImg: ImageStyle;
			loading: ViewStyle;
		} => ({
			column: {
				width: contentWidth,
			},
			titleBar: {
				width: contentWidth,
			},
			loadingImgView: {
				width: contentWidth,
			},
			loadingImg: {
				width: contentWidth - 50,
			},
			loadedImg: {
				width: contentWidth,
				height: windowHeight,
			},
			failedImg: {
				width: contentWidth,
			},
			loading: {
				top: windowHeight / 2 - 30,
				left: contentWidth / 2 - 15,
			},
		}),
		[contentWidth, windowHeight],
	);

	function init(startIndex: number, urls: string[]) {
		const len = urls.length;
		const temp = urls.map((url) => ({
			width: 0,
			height: 0,
			status: 'loading',
			url,
			scalable: new Animated.Value(1),
			animatedX: new Animated.Value(0),
		}));

		layoutImage.current = {};

		const mid = Math.floor((len - 1) / 2);
		const offset = mid - startIndex;
		const px = offset * contentWidth;
		const stdX = len % 2 === 0 ? px + contentWidth / 2 : px;

		positionX.current = px;
		standardPositionX.current = stdX;
		animatedPositionX.setValue(stdX);

		setImagesInfo(temp);
		setCurIndex(startIndex);
		setMidIndex(mid);
		setMaxIndex(len - 1);
		setImageLoaded(false);

		// We need to use a timeout here to let state settle before calling dependent functions
		setTimeout(() => {
			startRotate();
			fetchImage(startIndex, temp);

			Animated.parallel([
				Animated.timing(fadeAnim, {
					toValue: 1,
					duration: 300,
					easing: Easing.linear,
					useNativeDriver: true,
				}),
				Animated.timing(scalable, {
					toValue: 1,
					duration: 300,
					easing: Easing.linear,
					useNativeDriver: true,
				}),
			]).start(() => {
				imgScale.current = 1;
			});
		}, 0);
	}

	function updateImageInfo(idx: number, imageInfo: ImageInfo, currentImagesInfo: ImageInfo[]) {
		const updated = currentImagesInfo.slice();
		updated[idx] = imageInfo;
		setImagesInfo(updated);
		setImageLoaded(true);
	}

	function prefetchNeighbor(idx: number) {
		const info = imagesInfoRef.current[idx];
		if (info?.status !== 'loading') return;
		Image.prefetch(info.url).then(
			() => {
				Image.getSize(
					info.url,
					(w, h) => {
						const updated: ImageInfo = Object.assign({}, info, { width: w, height: h, status: 'success' });
						updateImageInfo(idx, updated, imagesInfoRef.current);
					},
					() => {
						/* ignore, user may never swipe here */
					},
				);
			},
			() => {
				/* ignore, user may never swipe here */
			},
		);
	}

	function fetchImage(idx: number, currentImagesInfo?: ImageInfo[]) {
		const info = currentImagesInfo ?? imagesInfoRef.current;
		if (info[idx]?.status === 'success') {
			setImageLoaded(true);
			prefetchNeighbor(idx - 1);
			prefetchNeighbor(idx + 1);
			return;
		}

		const imageInfo = Object.assign({}, info[idx]);
		const prefetchImagePromise = Image.prefetch(imageInfo.url);

		prefetchImagePromise.then(
			() => {
				Image.getSize(
					imageInfo.url,
					(w, h) => {
						imageInfo.width = w;
						imageInfo.height = h;
						imageInfo.status = 'success';
						updateImageInfo(idx, imageInfo, imagesInfoRef.current);
						prefetchNeighbor(idx - 1);
						prefetchNeighbor(idx + 1);
					},
					() => {
						imageInfo.status = 'fail';
						updateImageInfo(idx, imageInfo, imagesInfoRef.current);
					},
				);
			},
			() => {
				imageInfo.status = 'fail';
				updateImageInfo(idx, imageInfo, imagesInfoRef.current);
			},
		);
	}

	function startRotate() {
		rotateValue.setValue(0);

		if (imageLoadedRef.current) {
			rotateValue.setValue(1);
			return;
		}

		Animated.timing(rotateValue, {
			toValue: 1,
			duration: 800,
			useNativeDriver: true,
			easing: Easing.linear,
		}).start(() => startRotate());
	}

	function resetPosition() {
		Animated.timing(animatedPositionX, {
			toValue: standardPositionX.current,
			duration: 0,
			useNativeDriver: true,
			easing: Easing.linear,
		}).start(() => {
			positionX.current = standardPositionX.current;
		});
	}

	function next(ci: number) {
		const url = imageUrls[ci + 1];
		if (url) {
			standardPositionX.current -= contentWidth;
			imagesInfoRef.current[ci]!.scalable.setValue(1);
			imagesInfoRef.current[ci]!.animatedX.setValue(0);
			setCurIndex(ci + 1);
			setImageLoaded(false);
			callback(ci + 1);
		}
	}

	function prev(ci: number) {
		const url = imageUrls[ci - 1];
		if (url) {
			standardPositionX.current += contentWidth;
			imagesInfoRef.current[ci]!.scalable.setValue(1);
			imagesInfoRef.current[ci]!.animatedX.setValue(0);
			setCurIndex(ci - 1);
			setImageLoaded(false);
			callback(ci - 1);
		}
	}

	function callback(newIndex: number) {
		imgScale.current = 1;
		horizontalWholeCounter.current = 0;
		maxOffsetX.current = 0;
		isReachedBorder.current = false;
		fetchImage(newIndex);
		resetPosition();
		startRotate();
	}

	async function downloadCurrentImage(data: ImageInfo[]) {
		let url = data[curIndexRef.current]?.url ?? '';

		if (await mediaLibrary.requestPermission()) {
			if (Platform.OS === 'android') {
				const eventNameWithoutSpaceLowerCase = (eventName ?? '').replace(' ', '_').toLowerCase();
				const eventNameWithoutApostorphe = eventNameWithoutSpaceLowerCase.replace("'", '_');

				const imageStr =
					eventNameWithoutApostorphe + '_' + String(Math.floor(Math.random() * (999 - 100 + 1) + 100)) + '.jpg';

				const download = FileSystem.createDownloadResumable(url, FileSystem.documentDirectory + imageStr);
				url = await download.downloadAsync().then((item) => {
					return item !== undefined ? item.uri : '';
				});
			}
			try {
				await mediaLibrary.saveToLibrary(url);
				// Three platforms, three places the file ends up: the photo library on iOS, the
				// gallery on Android, and the browser's downloads on web.
				errorHandler(Translations.successDownloadedTitle, savedMessage());
			} catch {
				errorHandler(Translations.errorSomethingWentWrongShort, Translations.errorSomethingWentWrongGeneric);
			}
		}
	}

	function getImageList() {
		const ImageLists = imageUrls.map((imageUrl, idx) => {
			const imageInfo = imagesInfo[idx];
			if (!imageInfo) return null;

			let imgWidth = imageInfo.width;
			let imgHeight = imageInfo.height;
			const imgScalable = imageInfo.scalable;
			const imgAnimatedX = imageInfo.animatedX;

			if (imgWidth > contentWidth) {
				const widthPixel = contentWidth / imgWidth;
				imgWidth *= widthPixel;
				imgHeight *= widthPixel;
			}

			if (imgHeight > windowHeight) {
				const HeightPixel = windowHeight / imgHeight;
				imgWidth *= HeightPixel;
				imgHeight *= HeightPixel;
			}

			switch (imageInfo.status) {
				case 'loading':
					return (
						<View key={idx} style={[viewer.loadingImgView, sizedStyles.loadingImgView]}>
							<Image source={{ uri: imageUrl }} style={[viewer.loadingImg, sizedStyles.loadingImg]} />
						</View>
					);
				case 'success':
					return (
						<View key={idx} style={[viewer.loadedImg, sizedStyles.loadedImg]}>
							<Animated.View
								style={[
									viewer.animatedImgWrap,
									{
										width: imgWidth,
										height: imgHeight,
										transform: [{ scale: imgScalable }, { translateX: imgAnimatedX }],
									},
								]}
							>
								<Image
									source={{ uri: imageUrl }}
									style={[viewer.animatedImgInner, { width: imgWidth, height: imgHeight }]}
									onLayout={(e) => {
										layoutImage.current[idx] = e.nativeEvent.layout;
									}}
								/>
							</Animated.View>
						</View>
					);
				case 'fail':
					return (
						<View key={idx} style={[viewer.failedImg, sizedStyles.failedImg]}>
							<Image
								source={failedUrl ? { uri: failedUrl } : Images.placeholder}
								style={[viewer.failedImg, sizedStyles.failedImg]}
							/>
						</View>
					);
				default:
					return null;
			}
		});

		return ImageLists;
	}

	const spin = rotateValue.interpolate({
		inputRange: [0, 1],
		outputRange: ['0deg', '360deg'],
	});

	return (
		<Modal
			animationType={'fade'}
			hardwareAccelerated={true}
			transparent={true}
			visible={shown}
			onRequestClose={() => null}
		>
			<View style={viewer.root}>
				{/* The backdrop covers the whole window so nothing shows through, but the viewer itself stays
				    inside the app's column (decision D8). The absolutely positioned controls anchor to this
				    container rather than the window, which is also what keeps the spinner centred. */}
				<View style={[viewer.column, sizedStyles.column]}>
					<TouchableOpacity
						style={[viewer.closeBtn, { top: topInset + 5 }]}
						onPress={() => {
							onClose();
						}}
					>
						<MaterialIcons name="close" size={30} style={viewer.iconOverlay} />
					</TouchableOpacity>
					<TouchableOpacity
						style={[viewer.downloadBtn, { top: topInset + 5 }]}
						onPress={() => downloadCurrentImage(imagesInfo)}
					>
						<MaterialIcons name="file-download" size={30} style={viewer.iconOverlay} />
					</TouchableOpacity>
					<Animated.View
						style={[
							viewer.titleBar,
							sizedStyles.titleBar,
							{
								opacity: fadeAnim,
								marginTop: topInset,
							},
						]}
					>
						<Text style={viewer.title}>
							{curIndex + 1} / {imageUrls.length}
						</Text>
					</Animated.View>
					<Animated.View
						style={[
							viewer.container,
							{
								opacity: fadeAnim,
								transform: [{ scale: scalable }],
							},
						]}
						{...imagePanResponder.panHandlers}
					>
						<Animated.View
							style={[
								viewer.moveBox,
								{
									width: imageUrls.length * contentWidth,
									transform: [{ translateX: animatedPositionX }],
								},
							]}
						>
							{imagesInfo.length > 0 ? getImageList() : null}
						</Animated.View>
						{!imageLoaded ? (
							<View style={[viewer.loading, sizedStyles.loading]}>
								<View style={[viewer.common, viewer.outer]} />
								<Animated.View
									style={[
										viewer.common,
										viewer.inner,
										{
											transform: [{ rotate: spin }],
										},
									]}
								/>
							</View>
						) : null}
					</Animated.View>
				</View>
			</View>
		</Modal>
	);
}

const viewer = StyleSheet.create({
	root: {
		flex: 1,
		// Full-screen black background so nothing (e.g.
		backgroundColor: Colors.blackPure,
	},
	column: {
		flex: 1,
		alignSelf: 'center',
	},
	closeBtn: {
		position: 'absolute',
		backgroundColor: Colors.transparent,
		top: 25,
		right: 5,
		zIndex: 5,
		padding: Dimens.padding10,
	},
	downloadBtn: {
		position: 'absolute',
		top: 25,
		right: 50,
		zIndex: 5,
		backgroundColor: Colors.transparent,
		padding: Dimens.padding10,
	},
	iconOverlay: {
		color: Colors.white,
		backgroundColor: Colors.transparent,
	},
	animatedImgWrap: {},
	animatedImgInner: {},
	titleBar: {
		height: 40,
		backgroundColor: Colors.blackPure,
		justifyContent: 'center',
		alignItems: 'center',
	},
	title: {
		color: Colors.white,
		fontSize: Dimens.fontSize18,
		paddingTop: Dimens.padding15,
	},
	container: {
		flex: 1,
		justifyContent: 'center',
		alignItems: 'center',
		overflow: 'hidden',
		backgroundColor: Colors.blackPure,
	},
	moveBox: {
		flexDirection: 'row',
		alignItems: 'center',
	},
	loadingImgView: {
		height: 300,
		justifyContent: 'center',
		alignItems: 'center',
	},
	loadingImg: {
		height: 150,
	},
	loadedImg: {
		overflow: 'hidden',
		justifyContent: 'center',
		alignItems: 'center',
	},
	failedImg: {
		height: 300,
	},
	loading: {
		position: 'absolute',
	},
	common: {
		position: 'absolute',
		zIndex: 22,
		width: 30,
		height: 30,
		borderWidth: 3,
		borderRadius: 15,
	},
	outer: {
		borderColor: 'gray',
	},
	inner: {
		borderTopColor: Colors.whiteOverlay,
		borderRightColor: Colors.transparent,
		borderBottomColor: Colors.transparent,
		borderLeftColor: Colors.transparent,
	},
});
